<template>
  <section id="portfolio">
    <div class="container">

      <div id="employee_search">
        <div class="form-group row">
          <h3>Employee Search</h3>
        </div>

        <form id="search" @submit.prevent="onSearch">
          <div class="container text-end">
            <!-- Company -->
            <div class="form-group row justify-content-end">
              <span class="col-sm-3">
                <select v-model="filters.company_name" class="form-control text-end" @change="onCompanyChanged">
                  <option value="" disabled selected>اختر الشركة</option>
                  <option value="">— أي شركة —</option>
                  <option v-for="c in companies" :key="c.company_id" :value="c.company_name">
                    {{ c.company_name }}
                  </option>
                </select>
              </span>
            </div>

            <!-- Department (dependent on company if mapping available) -->
            <div class="form-group row justify-content-end">
              <span class="col-sm-3">
                <select v-model="filters.department_name" class="form-control text-end">
                  <option value="" disabled selected>اختر القسم</option>
                  <option value="">— أي قسم —</option>
                  <option v-for="d in departmentsFiltered" :key="d.department_id" :value="d.department_name">
                    {{ d.department_name }}
                  </option>
                </select>
              </span>
            </div>

            <!-- Names -->
            <div class="form-group row justify-content-end">
              <span class="col-sm-3">
                <input
                  type="text"
                  class="form-control text-end"
                  v-model.trim="filters.first_name"
                  placeholder="الاسم الأول"
                  @input="debouncedSearch()"
                  style="margin-bottom:6px;"
                />
                <input
                  type="text"
                  class="form-control text-end"
                  v-model.trim="filters.last_name"
                  placeholder="اسم العائلة"
                  @input="debouncedSearch()"
                />
              </span>
            </div>

            <div class="text-end">
              <input type="submit" class="btn btn-primary" value="بحث" />
            </div>
          </div>
        </form>
      </div>

      <div v-if="filters.tag_slug" class="tag-filter-banner">
        <span>فلتر الوسم مفعل: <strong>#{{ tagDisplayLabel }}</strong></span>
        <button type="button" class="btn btn-link" @click.prevent="clearTagFilter">
          إزالة الفلتر
        </button>
      </div>

      <!-- Loading reference data -->
      <div v-if="loadingRef" class="loader-wrap">
        <div class="spinner" aria-label="loading"></div>
        <span>Loading filters…</span>
      </div>

      <!-- Results loading -->
      <div v-if="loading" class="loader-wrap" style="margin-top:12px;">
        <div class="spinner" aria-label="loading"></div>
        <span>Searching…</span>
      </div>

      <!-- Pagination -->
      <ul class="pagination" id="page-number" v-if="!loading && totalPages > 1" style="margin-top:12px;">
        <li class="page-item" v-if="pageStart > 1">
          <a class="page-link" href="#" @click.prevent="prevPageBlock">Prev</a>
        </li>

        <li
          v-for="p in pageNumbersToShow"
          :key="p"
          class="page-item"
          :class="{ active: p === store.page }"
        >
          <a class="page-link myButton" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
        </li>

        <li class="page-item" v-if="pageEnd < totalPages">
          <a class="page-link" href="#" @click.prevent="nextPageBlock">Next</a>
        </li>
      </ul>

      <!-- Results -->
      <div id="active_search_result">
        <ul id="emp_list">
          <li v-for="emp in store.results" :key="emp.employee_id">
            <router-link :to="`/employee/${emp.employee_id}`">
              {{ emp.first_name }} {{ emp.last_name }}
            </router-link>
          </li>
          <li v-if="!loading && store.results.length === 0"><em>No results</em></li>
        </ul>
      </div>

    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue"
import { useRoute, useRouter } from 'vue-router'
import axios from "axios"
import { storeToRefs, defineStore } from 'pinia'

import { useEmployeeSearchStore } from '@/store/employeeSearch'


interface Company {
  company_id: number;
  company_name: string;
}
interface Department {
  department_id: number;
  department_name: string;
}
interface EmployeeSummary {
  employee_id: number;
  first_name: string;
  last_name: string;
}
interface CompanyDepartment {
  company_id: number;
  department_id: number;
}

const route = useRoute()
const router = useRouter()
const store = useEmployeeSearchStore()
const { filters } = storeToRefs(store)
const tagLabel = ref<string>("")
const tagDisplayLabel = computed(() => tagLabel.value || filters.value.tag_slug || "")

// dropdown data
const companies = ref<Company[]>([]);
const departments = ref<Department[]>([]);

// Optional mapping for dependent dropdowns.
// If your backend extends /api/employees/reference-data to include
// company_departments (array of { company_id, department_id }),
// we'll use it; otherwise department dropdown shows all.
const companyDepartments = ref<CompanyDepartment[]>([])

const total = ref(0)

const loading = ref(false)
const loadingRef = ref(false)

// page block window (like old pageLimit = 10)
const pageWindow = 10

// derived
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / store.limit)))

const pageStart = computed(() => {
  const block = Math.floor((store.page - 1) / pageWindow)
  return block * pageWindow + 1
})

const pageEnd = computed(() => Math.min(totalPages.value, pageStart.value + pageWindow - 1))

const pageNumbersToShow = computed(() => {
  const arr = []
  for (let p = pageStart.value; p <= pageEnd.value; p++) arr.push(p)
  return arr
})

const selectedCompany = computed(() =>
  companies.value.find(c => c.company_name === filters.value.company_name) || null
)

const departmentsFiltered = computed<Department[]>(() => {
  const sel = selectedCompany.value
  if (!sel) {
    // No company selected
    return departments.value
  }
  if (!companyDepartments.value?.length) {
    // No mapping data to filter on
    return departments.value
  }

  const deptIds = new Set<number>(
    companyDepartments.value
      .filter(cd => cd.company_id === sel.company_id)
      .map(cd => cd.department_id)
  )
  return departments.value.filter(d => deptIds.has(d.department_id))
})


function onCompanyChanged() {
  // Reset department when company changes
  filters.value.department_name = ""
  // Optional: auto-search when company changes
  debouncedSearch()
}

async function fetchReferenceData() {
  loadingRef.value = true
  try {
    const [companiesRes, departmentsRes] = await Promise.all([
      axios.get("/api/companies"),
      axios.get("/api/departments"),
    ])
    // Accept either raw arrays or nested keys, to be defensive
    const companiesData = Array.isArray(companiesRes.data)
      ? companiesRes.data
      : companiesRes.data?.companies ?? []
    const departmentsData = Array.isArray(departmentsRes.data)
      ? departmentsRes.data
      : departmentsRes.data?.departments ?? []

    companies.value = companiesData // [{ company_id, company_name, ... }]
    departments.value = departmentsData // [{ department_id, department_name, ... }]

    // Optional mapping stays empty unless you add an API for it
    companyDepartments.value = []
  } catch (err) {
    console.error("Failed loading reference data", err)
    companies.value = []
    departments.value = []
  } finally {
    loadingRef.value = false
  }
}

async function fetchEmployees() {
  loading.value = true
  try {
    await store.fetchPage()     // uses store.page + store.limit + filters
    total.value = store.total
  } finally {
    loading.value = false
  }
}

// actions
function onSearch() {
  if (store.page !== 1) {
    store.setPage(1)
  } else {
    fetchEmployees()
  }
}

function clearTagFilter() {
  if (!filters.value.tag_slug) return
  filters.value.tag_slug = ""
  tagLabel.value = ""
  onSearch()
}

function goToPage(p: any) {
  if (p < 1 || p > totalPages.value) return
  store.setPage(p)
}
function nextPageBlock() {
  const nextStart = pageEnd.value + 1
  if (nextStart <= totalPages.value) store.setPage(nextStart)
}
function prevPageBlock() {
  const prevStart = pageStart.value - pageWindow
  if (prevStart >= 1) store.setPage(prevStart)
  else store.setPage(1)
}

// react to page changes
watch(() => store.page, fetchEmployees)

// simple debounce for typing in name fields
let debounceTimer: any
function debouncedSearch(delay = 300) {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    onSearch()
  }, delay)
}

onMounted(async () => {
  const q = route.query
  store.setPage(q.page ? Number(q.page) : 1)
  const initialTag = (q.tag as string) || (q.tag_slug as string) || ""
  tagLabel.value = (q.tagLabel as string) || ""
  store.setFilters({
    company_name: (q.company_name as string) || "",
    department_name: (q.department_name as string) || "",
    first_name: (q.first_name as string) || "",
    last_name: (q.last_name as string) || "",
    tag_slug: initialTag,
  })
  await fetchReferenceData()
  fetchEmployees()
})
watch(
  () => [
    store.page,
    store.filters.company_name,
    store.filters.department_name,
    store.filters.first_name,
    store.filters.last_name,
    store.filters.tag_slug,
    tagLabel.value,
  ],
  ([page, company, dept, first, last, tagSlug, currentLabel]) => {
    const raw = {
      page,
      company_name: company,
      department_name: dept,
      first_name: first,
      last_name: last,
      tag: tagSlug,
      tagLabel: currentLabel,
    }
    const query = Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== "" && v != null)
    )
    router.replace({ query })
  },
  { deep: true }
)

watch(
  () => [route.query.tag, route.query.tagLabel],
  ([tagSlug, tagName]) => {
    const slug = (tagSlug as string) || ""
    const label = (tagName as string) || ""
    if (slug === store.filters.tag_slug && label === tagLabel.value) {
      return
    }
    store.setFilters({ tag_slug: slug })
    tagLabel.value = label
    onSearch()
  }
)
</script>

<style scoped>
.myButton { cursor: pointer; }
.pagination > .active > .page-link,
.pagination > .active > a {
  text-decoration: underline;
}

/* Simple loader */
.loader-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.spinner {
  width: 16px; height: 16px;
  border: 2px solid #ccc;
  border-top-color: #555;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.tag-filter-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  margin: 12px 0;
}

.tag-filter-banner strong {
  color: #0f172a;
}
</style>
